import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowRight, Check } from "lucide-react";
import { api } from "../console/api";
import MoisCalendrier, { moisDe } from "./MoisCalendrier";
import { franc } from "../lib/reglesPrives";

/* « Modifier le cours » d'un cours privé réservé : la console (fenêtre) et l'app école
   (feuille du bas) — Jen, 02.11.2026 : « Oui l'école doit pouvoir déplacer les cours privés —
   l'école doit pouvoir tout faire — modifier, annuler etc. ». On y change :
   · la discipline, le prof (ceux qui la donnent), la durée (avec son prix, le barème) ;
   · le moment : « Ses heures libres » d'abord — le mois et les heures du prof choisi, comme la
     famille sur sa page (mais dès maintenant) — ou « Autre heure », hors de ses
     disponibilités : l'école décide. Le serveur vérifie chaque moment choisi et le dit tout
     de suite (« Lydia donne son cours … », « La salle est prise … ») ;
   · la note (ce que l'élève aimerait travailler), un mot pour la famille, et « Prévenir la
     famille par e-mail » (cochée d'office).
   Sans la pause entre deux cours (Réglages › Cours privés, 15 minutes) : possible, l'école décide
   (Jen, 02.11.2026 après-midi) — l'avertissement s'affiche, et « Enregistrer » attend la case
   « Placer sans la pause ». Un cours au même moment dans la salle, ou un cours de l'école du prof :
   toujours refusé.
   Le récapitulatif « Avant → Après » dit ce qui change, et qui reçoit quoi. `useModification`
   garde l'état et parle au serveur ; `ContenuModification` l'affiche (les deux écrans posent
   leurs propres boutons). */

const heureFr = (h) => { const [a, b] = String(h || "").split(":"); return `${Number(a)}h${b && b !== "00" ? b : ""}`; };
const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const jourLong = (iso) => { const d = new Date(`${iso}T12:00:00`); return `${JOURS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]}`; };
const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
const enMin = (h) => { const [a, b] = String(h || "0:0").split(":").map(Number); return a * 60 + (b || 0); };
const plus = (h, m) => { const t = enMin(h) + Number(m || 0); return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };
const aujourdhui = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Zurich" });

export function useModification(r, onFait) {
  const [disc, setDisc] = useState(r.discipline);
  const [prof, setProf] = useState(r.prof_id);
  const [minutes, setMinutes] = useState(Number(r.minutes));
  const [jour, setJour] = useState(r.date);
  const [debut, setDebut] = useState(r.debut);
  const [mode, setMode] = useState("libres");
  const [objectif, setObjectif] = useState(r.objectif || "");
  const [mot, setMot] = useState("");
  const [prevenir, setPrevenir] = useState(true);
  // « Placer sans la pause » : à cocher pour un moment trop près d'un autre cours privé.
  const [sansPause, setSansPause] = useState(false);
  useEffect(() => { setSansPause(false); }, [disc, prof, minutes, jour, debut]);
  const [d, setD] = useState(null);                // la réponse du serveur : options, heures libres, vérification
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [mois, setMois] = useState(moisDe(r.date));
  // Seule la réponse du DERNIER choix compte (comme la page de réservation du site) : un prof
  // touché juste après une durée ne doit pas recevoir les heures de l'autre demande.
  const tour = useRef(0);
  useEffect(() => {
    const n = ++tour.current;
    setCharge(true);
    const q = new URLSearchParams({ discipline: disc, minutes: String(minutes), prof, jour: jour || "", debut: debut || "" });
    const t = setTimeout(() => {
      api(`/reservations-privees/${r.id}/modifier?${q.toString()}`)
        .then((x) => { if (n === tour.current) { setD(x); setCharge(false); } })
        .catch((e) => { if (n === tour.current) { setErreur(e.message); setCharge(false); } });
    }, d ? 250 : 0);
    return () => clearTimeout(t);
  }, [r.id, disc, prof, minutes, jour, debut]); // eslint-disable-line react-hooks/exhaustive-deps

  const disciplines = d?.disciplines || [];
  const discipline = disciplines.find((x) => x.id === disc);
  const profs = discipline?.profs || [];
  const durees = discipline?.durees || [];
  // Le prix d'un cours déjà réservé ne change pas tant que sa discipline et sa durée restent
  // (même si le barème des Réglages a changé depuis) : le serveur le garde aussi.
  const prix = disc === r.discipline && Number(minutes) === Number(r.minutes)
    ? Number(r.montant) : durees.find((x) => x.minutes === minutes)?.prix;
  const nomProf = (pid) => disciplines.flatMap((x) => x.profs).find((p) => p.id === pid)?.prenom || r.prof;
  // Une autre discipline : le prof reste s'il la donne, sinon le premier qui la donne ; la
  // durée reste si elle existe, sinon la première.
  const choisirDiscipline = (id) => {
    const x = disciplines.find((y) => y.id === id);
    if (!x) return;
    setErreur(""); setDisc(id);
    if (!x.profs.some((p) => p.id === prof)) setProf(x.profs[0]?.id || "");
    if (!x.durees.some((y) => y.minutes === minutes)) setMinutes(x.durees[0]?.minutes || 0);
  };
  const choisirJour = (j) => { setErreur(""); setJour(j); setDebut(""); };
  const choisirHeure = (h) => { setErreur(""); setDebut(h); };

  const parJour = useMemo(() => {
    const m = {};
    (d?.creneaux || []).forEach((c) => { (m[c.date] ||= []).push(c); });
    return m;
  }, [d]);
  const joursCal = useMemo(() => Object.fromEntries(Object.keys(parJour).map((j) => [j, { points: [] }])), [parJour]);
  const horaireChange = jour !== r.date || debut !== r.debut;
  const coursChange = horaireChange || prof !== r.prof_id || Number(minutes) !== Number(r.minutes) || disc !== r.discipline;
  const change = coursChange || objectif.trim() !== String(r.objectif || "").trim();
  const v = d?.verification;
  // Seule la note change : le moment n'a pas à être revérifié (le serveur ne le fait pas non plus).
  const pret = change && !!jour && !!debut && (!coursChange || (!!v?.possible && (!v?.avertissement || sansPause)))
    && !charge && !envoi && d?.modifiable;
  const enregistrer = async () => {
    if (!pret) return;
    setEnvoi(true); setErreur("");
    try {
      const x = await api(`/reservations-privees/${r.id}/modifier`, { method: "POST", body: JSON.stringify({
        date: jour, debut, minutes, prof_id: prof, discipline: disc, objectif, message: mot, prevenir, sans_pause: sansPause }) });
      onFait(x);
    } catch (e) {
      setErreur(e.message); setEnvoi(false);
      // Refusé (l'heure vient d'être prise…) : les heures libres sont relues.
      tour.current += 1;
      const n = tour.current;
      const q = new URLSearchParams({ discipline: disc, minutes: String(minutes), prof, jour: jour || "", debut: debut || "" });
      api(`/reservations-privees/${r.id}/modifier?${q.toString()}`).then((x) => { if (n === tour.current) setD(x); }).catch(() => {});
    }
  };
  return { r, d, charge, erreur, envoi, pret, change, coursChange, horaireChange, disc, prof, minutes, jour, debut, mode,
    objectif, mot, prevenir, mois, prix, profs, durees, disciplines, parJour, joursCal, v, nomProf, sansPause,
    setMode, setObjectif, setMot, setPrevenir, setMois, setSansPause, setProf: (p) => { setErreur(""); setProf(p); },
    setMinutes: (m) => { setErreur(""); setMinutes(m); }, choisirDiscipline, choisirJour, choisirHeure, setJour, setDebut, enregistrer };
}

/* Une rangée de pastilles à toucher (discipline, prof, durée). */
export const Pastilles = ({ options, valeur, onChange, testid, mobile }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const on = o.valeur === valeur;
      return (
        <button key={o.valeur} type="button" onClick={() => onChange(o.valeur)} data-testid={`${testid}-${o.valeur}`} aria-pressed={on}
          className={`rounded-full border inline-flex items-center gap-1.5 font-medium transition-colors ${mobile ? "px-3.5 min-h-[40px] text-[14px]" : "px-3 py-1.5 text-xs"}`}
          style={on ? { background: o.couleur || "var(--primary)", borderColor: o.couleur || "var(--primary)", color: o.couleur ? "#fff" : "#04202A" }
            : { borderColor: "var(--border)", background: "#fff" }}>
          {o.couleur && <span className="w-2 h-2 rounded-full" style={{ background: on ? "#fff" : o.couleur }} />}
          {o.label}{o.detail && <span className={on ? "opacity-80" : "text-[var(--muted)]"}> · {o.detail}</span>}
        </button>
      );
    })}
  </div>
);

export const Titre = ({ children, mobile }) => (
  <div className={`font-semibold ${mobile ? "text-[13px] uppercase tracking-wide text-[var(--muted)]" : "text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]"} mb-1.5`}>{children}</div>
);

export function ContenuModification({ m, mobile = false }) {
  const { r, d } = m;
  if (!d) return <p className="text-sm text-[var(--muted)] py-6">{m.erreur || "Chargement…"}</p>;
  if (!d.modifiable) return <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="modif-impossible">{d.raison}</p>;
  const champ = mobile ? "input-field text-[15px] w-full" : "input-field text-sm w-full";
  const heures = m.parJour[m.jour] || [];
  const nouveauProf = m.nomProf(m.prof);
  const discLabel = m.disciplines.find((x) => x.id === m.disc)?.label || "";
  const minJour = Object.keys(m.parJour)[0];
  const maxJour = Object.keys(m.parJour).slice(-1)[0];
  return (
    <div className={mobile ? "space-y-4" : "space-y-4 text-sm"} data-testid="modif-cours">
      <div>
        <Titre mobile={mobile}>Discipline</Titre>
        <Pastilles mobile={mobile} testid="modif-discipline" valeur={m.disc} onChange={m.choisirDiscipline}
          options={m.disciplines.map((x) => ({ valeur: x.id, label: x.label }))} />
      </div>
      <div className={mobile ? "space-y-4" : "grid md:grid-cols-2 gap-4"}>
        <div>
          <Titre mobile={mobile}>Prof</Titre>
          <Pastilles mobile={mobile} testid="modif-prof" valeur={m.prof} onChange={m.setProf}
            options={m.profs.map((p) => ({ valeur: p.id, label: p.prenom, couleur: p.couleur }))} />
        </div>
        <div>
          <Titre mobile={mobile}>Durée</Titre>
          <Pastilles mobile={mobile} testid="modif-duree" valeur={m.minutes} onChange={m.setMinutes}
            options={m.durees.map((x) => ({ valeur: x.minutes, label: `${x.minutes} min`, detail: `CHF ${franc(x.prix)}` }))} />
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Titre mobile={mobile}>Quand ?</Titre>
          {/* Ses heures libres d'abord ; « Autre heure » : l'école peut sortir de ses disponibilités. */}
          <div className="inline-flex rounded-full p-0.5" style={{ background: "#F1F5F9" }} role="tablist">
            {[["libres", `Heures libres de ${nouveauProf}`], ["autre", "Autre heure"]].map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={m.mode === k} onClick={() => m.setMode(k)} data-testid={`modif-mode-${k}`}
                className={`rounded-full font-medium ${mobile ? "px-3 min-h-[34px] text-[13px]" : "px-3 py-1 text-xs"}`}
                style={m.mode === k ? { background: "#fff", boxShadow: "0 1px 3px rgba(15,27,45,0.12)" } : { color: "var(--muted)" }}>{l}</button>
            ))}
          </div>
        </div>
        {m.mode === "libres" ? (
          !Object.keys(m.parJour).length && !m.charge ? (
            <p className="text-[var(--muted)]" data-testid="modif-aucune-heure">
              Aucune heure libre pour {nouveauProf} {d.regles?.horizon_texte && d.regles.horizon_texte !== "2 mois" ? `ces ${d.regles.horizon_texte}` : "ces deux prochains mois"}. Choisis « Autre heure » : l'école décide.
            </p>
          ) : (
            <div className={mobile ? "space-y-3" : "grid md:grid-cols-[300px_1fr] gap-4 items-start"}>
              <MoisCalendrier mois={m.mois} onMois={m.setMois} jours={m.joursCal} choisi={m.jour} onChoisir={m.choisirJour}
                testid="modif-jours" min={minJour < r.date ? minJour : r.date} max={maxJour > r.date ? maxJour : r.date} aujourdhui={aujourdhui()} />
              <div>
                <div className="font-medium mb-2">{m.jour ? jourLong(m.jour).replace(/^./, (c) => c.toUpperCase()) : "Choisis un jour en vert"}</div>
                <div className="flex flex-wrap gap-2" data-testid="modif-heures">
                  {heures.map((c) => {
                    const on = c.debut === m.debut;
                    const actuel = m.jour === r.date && c.debut === r.debut && m.prof === r.prof_id;
                    return (
                      <button key={c.debut} type="button" onClick={() => m.choisirHeure(c.debut)} data-testid={`modif-heure-${c.debut}`} aria-pressed={on}
                        className={`rounded-full border font-semibold tabular-nums ${mobile ? "px-4 min-h-[42px] text-[15px]" : "px-3.5 py-1.5 text-sm"}`}
                        style={on ? { background: "var(--primary)", borderColor: "var(--primary)", color: "#04202A" } : { borderColor: "var(--border)", background: "#fff" }}>
                        {c.debut}{actuel && <span className="font-normal text-[11px] ml-1">(actuel)</span>}
                      </button>
                    );
                  })}
                  {m.jour && !heures.length && !m.charge && <p className="text-[var(--muted)]">Pas d'heure libre ce jour-là : choisis un jour en vert, ou « Autre heure ».</p>}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-wrap gap-3 items-end">
            <label className="block"><span className="block text-xs text-[var(--muted)] mb-1">Date</span>
              <input type="date" className={champ} style={{ minWidth: 160 }} min={aujourdhui()} value={m.jour} onChange={(e) => m.setJour(e.target.value)} data-testid="modif-date" /></label>
            <label className="block"><span className="block text-xs text-[var(--muted)] mb-1">Heure de début</span>
              <input type="time" className={champ} style={{ minWidth: 120 }} step={300} min={d.regles?.ouverture || "07:00"}
                max={plus(d.regles?.fermeture || "22:00", -30)} value={m.debut} onChange={(e) => m.setDebut(e.target.value)} data-testid="modif-heure" /></label>
            {m.debut && <span className="text-[var(--muted)] pb-2">→ fin à {heureFr(plus(m.debut, m.minutes))}</span>}
          </div>
        )}
        {/* Ce moment est-il possible ? Le serveur le dit à chaque choix. */}
        {m.coursChange && m.jour && m.debut && m.v && !m.charge && (
          <p className="mt-2 flex items-start gap-1.5 rounded-xl px-3 py-2" data-testid="modif-verif" data-possible={m.v.possible ? "oui" : "non"}
            style={m.v.possible ? { background: "#F0FDF4", color: "#15803D" } : { background: "#FEF2F2", color: "#B91C1C" }}>
            {m.v.possible ? <Check size={15} className="shrink-0 mt-0.5" /> : <AlertCircle size={15} className="shrink-0 mt-0.5" />}
            <span>{m.v.possible
              ? (m.v.dans_ses_dispos ? `Libre : ${m.v.quand}, dans les disponibilités de ${nouveauProf}.`
                : `Possible : ${m.v.quand}, hors des disponibilités de ${nouveauProf} — l'école décide (la salle et ses cours sont libres).`)
              : m.v.message}</span>
          </p>
        )}
        {/* Sans la pause entre deux cours : l'école peut passer outre, en le confirmant. */}
        {m.coursChange && m.jour && m.debut && m.v?.possible && m.v.avertissement && !m.charge && (
          <div className="mt-2 rounded-xl px-3 py-2 space-y-2" style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A" }}
            data-testid="modif-sans-pause-avertissement">
            <p className="flex items-start gap-1.5"><AlertCircle size={15} className="shrink-0 mt-0.5" /><span>{m.v.avertissement}</span></p>
            <label className={`flex items-start gap-2 font-medium ${mobile ? "text-[15px]" : ""}`}>
              <input type="checkbox" className={mobile ? "mt-0.5 w-5 h-5 shrink-0" : "mt-0.5"} checked={m.sansPause}
                onChange={(e) => m.setSansPause(e.target.checked)} data-testid="modif-sans-pause" />
              <span>Placer sans la pause (je confirme)</span>
            </label>
          </div>
        )}
      </div>

      <label className="block"><Titre mobile={mobile}>Ce que l'élève aimerait travailler</Titre>
        <textarea className={champ} rows={2} value={m.objectif} onChange={(e) => m.setObjectif(e.target.value)} placeholder="(facultatif)" data-testid="modif-objectif" /></label>
      <label className="block"><Titre mobile={mobile}>Un mot pour la famille</Titre>
        <textarea className={champ} rows={2} value={m.mot} onChange={(e) => m.setMot(e.target.value)} placeholder="Facultatif — il apparaît dans l'e-mail" data-testid="modif-mot" /></label>
      <label className={`flex items-start gap-2.5 ${mobile ? "text-[15px]" : ""}`}>
        <input type="checkbox" className={mobile ? "mt-0.5 w-5 h-5 shrink-0" : "mt-0.5"} checked={m.prevenir} onChange={(e) => m.setPrevenir(e.target.checked)} data-testid="modif-prevenir" />
        <span>Prévenir la famille par e-mail <span className="text-[var(--muted)]">({r.email})</span></span>
      </label>

      {m.change && m.jour && m.debut && (
        <div className="rounded-2xl p-3.5 space-y-1.5" style={{ background: "var(--primary-tint)" }} data-testid="modif-recap">
          <div className="text-[var(--muted)] line-through decoration-1">{r.discipline_label} avec {r.prof} · {r.quand} ({r.minutes} min) · {chf(r.montant)}</div>
          <div className="font-semibold flex items-start gap-1.5"><ArrowRight size={15} className="shrink-0 mt-0.5" />
            <span>{discLabel} avec {nouveauProf} · {jourLong(m.jour)} à {heureFr(m.debut)} ({m.minutes} min) · {chf(m.prix)}</span></div>
          <div className="text-xs text-[var(--muted)]">
            {m.prevenir ? "La famille reçoit un e-mail avec le nouvel horaire pour son agenda. " : "La famille n'est pas prévenue par e-mail. "}
            {m.prof !== r.prof_id ? `${r.prof} reçoit l'annulation, ${nouveauProf} la réservation (avec l'agenda).` : `${r.prof} est prévenu·e (avec l'agenda).`}
            {" "}L'école reçoit une copie.
          </div>
        </div>
      )}
      {m.erreur && <p className="rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }} data-testid="modif-erreur">{m.erreur}</p>}
    </div>
  );
}
